import { MeshBuilder, StandardMaterial, TransformNode, type Mesh } from "@babylonjs/core";
import { HeroId, Slot, fxk, hv, pk, zk } from "@rivalwatch/shared";
import * as loops from "../../audio/cues/loops.ts";
import type { Out } from "../../audio/engine.ts";
import { bell, brass, click, noise, thump, tone, whoosh } from "../../audio/synth.ts";
import type { ClientHero, FxCtx, ProjectileFrame, ZoneFrame } from "../types.ts";

const V = { force: 0, awakened: 1, thrown: 2, ultDrop: 3 } as const;
const PK = { Hammer: pk(HeroId.Thud, 0), Spark: pk(HeroId.Thud, 1) } as const;
const ZK = { Realm: zk(HeroId.Thud, 0) } as const;
const FX = { Hammer: fxk(HeroId.Thud, 0), Thunder: fxk(HeroId.Thud, 1), Realm: fxk(HeroId.Thud, 2) } as const;

const hammerCue = (o: Out) => {
  thump(o, 0.95, 88, 40, 0.18);
  click(o, 0.42, 1500, 0.006);
  whoosh(o, 0.5, 360, 1800, 0.22);
  return 0.42;
};
const thunderCue = (o: Out) => {
  click(o, 0.45, 4200, 0.003);
  noise(o, { color: "crackle", type: "bandpass", f: 2600, gain: 0.7, d: 0.28 });
  bell(o, 880, 0.36, 0.35);
  thump(o, 0.72, 55, 28, 0.35);
  return 0.8;
};
const runeCue = (o: Out) => {
  tone(o, { type: "sawtooth", f: 110, f1: 440, glide: 0.28, gain: 0.08, d: 0.45, lp: 1200 });
  bell(o, 660, 0.35, 0.45);
  return 0.7;
};
const theme = {
  bpm: 96,
  octave: -1,
  voice: brass,
  notes: [
    [0, 0.75],
    [7, 0.25],
    [9, 0.5],
    [12, 0.75],
    [7, 0.5],
    [0, 1],
  ],
} satisfies ClientHero["audio"]["theme"];

const hero: ClientHero<{ handle: Mesh; hammer: Mesh; glow: Mesh; cape: Mesh; wingL: Mesh; wingR: Mesh }> = {
  id: HeroId.Thud,
  bulk: 1.2,
  shape: { head: 1.03, torso: 1.16, arms: 1.18, legs: 1.04 },
  gear(g) {
    const { scene, rig, add, toon, glow } = g;
    const handle = add(MeshBuilder.CreateCylinder("thud-handle", { diameter: 0.12, height: 0.95, tessellation: 10 }, scene), rig.handR, toon("#563c27"));
    handle.rotation.x = Math.PI / 2;
    handle.position.z = -0.34;
    const hammer = add(MeshBuilder.CreateBox("thud-hammer", { width: 0.62, height: 0.36, depth: 0.34 }, scene), rig.handR, toon("#b6c4d8"));
    hammer.position.z = -0.86;
    const strap = add(MeshBuilder.CreateTorus("thud-strap", { diameter: 0.22, thickness: 0.025, tessellation: 12 }, scene), rig.handR, toon("#2a1a13"));
    strap.rotation.x = Math.PI / 2;
    strap.position.z = 0.15;
    const spark = add(MeshBuilder.CreateTorus("thud-hammer-glow", { diameter: 0.68, thickness: 0.025, tessellation: 32 }, scene), rig.handR, glow("#8fd8ff", 0.75));
    spark.position.z = -0.86;
    spark.rotation.x = Math.PI / 2;
    const cape = add(MeshBuilder.CreateBox("thud-cape", { width: 0.72, height: 1.1, depth: 0.045 }, scene), rig.torso, toon("#7f2437"));
    cape.position.set(0, -0.04, 0.4);
    const wingL = add(MeshBuilder.CreateBox("thud-helmet-wing-l", { width: 0.08, height: 0.3, depth: 0.34 }, scene), rig.head, toon("#d7e4f5"));
    wingL.position.set(-0.34, 0.08, -0.02);
    wingL.rotation.z = -0.45;
    const wingR = add(MeshBuilder.CreateBox("thud-helmet-wing-r", { width: 0.08, height: 0.3, depth: 0.34 }, scene), rig.head, toon("#d7e4f5"));
    wingR.position.set(0.34, 0.08, -0.02);
    wingR.rotation.z = 0.45;
    return { handle, hammer, glow: spark, cape, wingL, wingR };
  },
  animate(gear, f) {
    const force = f.v[V.force] / 3;
    const awake = f.v[V.awakened] > 0;
    const thrown = f.v[V.thrown] > 0;
    gear.handle.setEnabled(!thrown);
    gear.hammer.setEnabled(!thrown);
    gear.glow.setEnabled(!thrown && (force > 0.05 || awake));
    gear.glow.scaling.setAll(0.8 + force * 0.35 + (awake ? Math.sin(f.t * 24) * 0.08 + 0.28 : 0));
    gear.hammer.rotation.z = awake ? Math.sin(f.t * 20) * 0.18 : 0;
    gear.cape.rotation.x = -0.12 - Math.min(0.25, f.speed / 40);
    if (f.ultActive || f.v[V.ultDrop] > 0) return "holding-both";
    if (f.action === 1 || f.action === 2) return "attack-melee-right";
    if (f.action === 4) return "holding-both-shoot";
  },
  hud(c, eid) {
    return [
      { label: "Thunder", value: hv(c, eid, V.force) / 3, color: "#8fd8ff" },
      { label: "Awake", value: hv(c, eid, V.awakened) / 300, color: "#d6f4ff" },
    ];
  },
  projectiles: {
    [PK.Hammer](scene, _f: ProjectileFrame) {
      const root = new TransformNode("thud-hammer-fly-root", scene);
      const metal = new StandardMaterial("thud-hammer-fly-metal", scene);
      metal.diffuseColor.set(0.65, 0.75, 0.9);
      metal.emissiveColor.set(0.05, 0.18, 0.32);
      const wood = new StandardMaterial("thud-hammer-fly-wood", scene);
      wood.diffuseColor.set(0.3, 0.2, 0.13);
      const head = MeshBuilder.CreateBox("thud-hammer-fly-head", { width: 0.72, height: 0.36, depth: 0.36 }, scene);
      head.parent = root;
      head.material = metal;
      const handle = MeshBuilder.CreateCylinder("thud-hammer-fly-handle", { diameter: 0.09, height: 0.9, tessellation: 10 }, scene);
      handle.parent = root;
      handle.material = wood;
      handle.rotation.x = Math.PI / 2;
      handle.position.z = 0.28;
      const pommel = MeshBuilder.CreateCylinder("thud-hammer-fly-pommel", { diameter: 0.16, height: 0.08, tessellation: 10 }, scene);
      pommel.parent = root;
      pommel.material = metal;
      pommel.rotation.x = Math.PI / 2;
      pommel.position.z = 0.78;
      return {
        gear: root,
        update(m: TransformNode, frame) {
          m.position.set(frame.pos.x, frame.pos.y, frame.pos.z);
          m.rotation.set(frame.age * 18, frame.age * 11, 0);
        },
        dispose() {
          root.dispose(false, true);
          metal.dispose();
          wood.dispose();
        },
      };
    },
  },
  zones: {
    [ZK.Realm](scene, f: ZoneFrame) {
      const mat = new StandardMaterial("thud-realm-mat", scene);
      mat.emissiveColor.set(0.25, 0.55, 1);
      mat.alpha = 0.35;
      const ring = MeshBuilder.CreateTorus("thud-realm", { diameter: f.radius * 2, thickness: 0.05, tessellation: 64 }, scene);
      ring.material = mat;
      return {
        gear: ring,
        update(m: typeof ring, frame) {
          m.position.set(frame.pos.x, frame.pos.y + 0.05, frame.pos.z);
          m.rotation.y += frame.dt * 1.5;
          m.scaling.setAll(1 + Math.sin(frame.age * 8) * 0.03);
        },
        dispose() {
          ring.dispose();
          mat.dispose();
        },
      };
    },
  },
  fx: {
    [FX.Thunder]: {
      impact(c: FxCtx, pos) {
        const bolt = MeshBuilder.CreateCylinder("thud-lightning-hit", { diameterTop: 0.02, diameterBottom: 0.18, height: 1.3, tessellation: 5 }, c.scene);
        bolt.position.set(pos.x, pos.y + 0.65, pos.z);
        bolt.material = c.glow("#d6f4ff", 0.85);
        c.timed(bolt, 0.22, (t, m) => {
          m.scaling.y = 1 - t * 0.6;
          m.rotation.y += 0.4;
        });
      },
      explosion(c: FxCtx, pos, radius) {
        const ring = MeshBuilder.CreateTorus("thud-thunder-burst", { diameter: radius * 2, thickness: 0.09, tessellation: 64 }, c.scene);
        ring.position.set(pos.x, pos.y + 0.08, pos.z);
        ring.material = c.glow("#d6f4ff", 0.7);
        c.timed(ring, 0.55, (t, m) => m.scaling.setAll(0.18 + t));
      },
      keepGeneric: true,
    },
  },
  audio: {
    weight: "heavy",
    fire: () => ({ key: "fire:thud:hammer", cue: hammerCue }),
    ability: {
      [Slot.Secondary]: hammerCue,
      [Slot.Ability1]: hammerCue,
      [Slot.Ability2]: thunderCue,
      [Slot.Ability3]: runeCue,
      [Slot.Ultimate]: thunderCue,
    },
    theme,
    themeTrim: { heroic: 0, hostile: -6.8, tease: -1.5 },
    loops: ({ v, alive }) => (alive && v[V.awakened] > 0 ? [{ key: "awake", build: loops.ward, gain: 0.55, value: v[V.awakened] / 300 }] : []),
    flight: { [PK.Hammer]: loops.flight("hook"), [PK.Spark]: loops.flight("orb") },
    impacts: { [FX.Hammer]: hammerCue, [FX.Thunder]: thunderCue },
    explosions: { [FX.Thunder]: () => thunderCue },
    zoneLoops: { [ZK.Realm]: { build: loops.portal, gain: 0.45, range: 36 } },
  },
  icons: {
    [Slot.Primary]: "M6 17l5-10 7 4-5 10z",
    [Slot.Secondary]: "M4 12h12M14 6l6 6-6 6M7 8l3 8",
    [Slot.Ability1]: "M4 14l8-10v7h8l-8 9v-6z",
    [Slot.Ability2]: ["M12 3v6l4-2-4 14v-6l-4 2z", "M4 19c4-3 12-3 16 0"],
    [Slot.Ability3]: "M12 3l3 7h6l-5 4 2 7-6-4-6 4 2-7-5-4h6z",
    [Slot.Ultimate]: "M12 2v8M7 10l5 5 5-5M4 20h16M6 16h12",
    passive: "M5 18c2-7 12-7 14 0M9 8l3-5 3 5",
  },
};

export default hero;
